import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShieldCheck, Mail, Phone, MapPin, Building2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; }
}

export default function PrivacyPolicy() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/menu/${slug}/privacy`);
        setData(res.data);
      } catch (e) { setError(e?.response?.data?.detail || "Impossibile caricare l'informativa"); }
    })();
  }, [slug]);

  if (error) return <div className="min-h-screen grid place-items-center bg-[#FBF9F5] text-[#C85A32] p-6 text-center">{error}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center bg-[#FBF9F5] text-[#6B625B]">Caricamento informativa...</div>;

  const { restaurant, controller } = data;
  const controllerName = controller.company_name || restaurant.name;
  const today = new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#FBF9F5] text-[#231F1D] pb-20">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-6">
        <Link to={`/menu/${slug}`} data-testid="privacy-back-link" className="inline-flex items-center gap-1.5 text-sm text-[#6B625B] hover:text-[#231F1D] font-semibold">
          <ArrowLeft size={14}/> Torna al menu
        </Link>

        <header className="mt-6 flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#3B503A] to-[#2f4030] grid place-items-center text-white shadow"><ShieldCheck size={22}/></div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-[#C85A32]">Informativa Privacy · GDPR</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-black leading-tight">{restaurant.name}</h1>
          </div>
        </header>

        {/* Titolare del trattamento */}
        <section data-testid="privacy-controller-card" className="mt-6 bg-white border border-[#E8E2D5] rounded-3xl p-5 shadow-sm">
          <h2 className="font-serif-display text-lg font-bold flex items-center gap-2"><Building2 size={18} className="text-[#C85A32]"/> Titolare del trattamento</h2>
          <div className="mt-3 space-y-1.5 text-sm">
            <div><b>Ragione sociale:</b> {controllerName}</div>
            {controller.legal_representative && <div><b>Legale rappresentante:</b> {controller.legal_representative}</div>}
            {controller.vat_number && <div><b>Partita IVA / C.F.:</b> {controller.vat_number}</div>}
            {controller.company_address && (
              <div className="flex items-start gap-1.5"><MapPin size={13} className="mt-0.5 text-[#6B625B]"/> {controller.company_address}</div>
            )}
            {controller.email && (
              <div className="flex items-center gap-1.5"><Mail size={13} className="text-[#6B625B]"/> <a href={`mailto:${controller.email}`} className="text-[#C85A32] underline">{controller.email}</a></div>
            )}
            {controller.phone && (
              <div className="flex items-center gap-1.5"><Phone size={13} className="text-[#6B625B]"/> <a href={`tel:${controller.phone}`} className="text-[#C85A32] underline">{controller.phone}</a></div>
            )}
          </div>
        </section>

        <article className="prose prose-sm max-w-none mt-6 text-[#231F1D] space-y-6 leading-relaxed">
          <section>
            <h2 className="font-serif-display text-xl font-bold">1. Finalità del trattamento</h2>
            <p className="text-sm text-[#3B3733]">Ai sensi degli artt. 13 e 14 del <b>Regolamento UE 2016/679 (GDPR)</b>, <b>{controllerName}</b> ("Titolare") informa che i dati personali che il cliente ("Interessato") fornisce al momento della registrazione al tavolo tramite il servizio VOICEMENU sono trattati per le seguenti finalità:</p>
            <ul className="list-disc pl-5 text-sm text-[#3B3733] space-y-1 mt-2">
              <li>Erogazione dei servizi digitali del locale (menu, quiz, chat, ordini);</li>
              <li>Invio di comunicazioni promozionali, offerte, eventi e novità del locale (marketing diretto);</li>
              <li>Analisi statistica anonima delle preferenze per migliorare l'esperienza cliente;</li>
              <li>Adempimento di obblighi contabili, fiscali e di legge.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif-display text-xl font-bold">2. Base giuridica</h2>
            <p className="text-sm text-[#3B3733]">Il trattamento per finalità di marketing e comunicazioni promozionali si basa sul <b>consenso libero, specifico e informato</b> prestato dall'Interessato al momento della registrazione (art. 6 §1 lett. a GDPR). Il consenso può essere revocato in qualsiasi momento senza pregiudicare la liceità del trattamento effettuato precedentemente.</p>
          </section>

          <section>
            <h2 className="font-serif-display text-xl font-bold">3. Categorie di dati trattati</h2>
            <ul className="list-disc pl-5 text-sm text-[#3B3733] space-y-1">
              <li>Nome (o pseudonimo)</li>
              <li>Contatto: email o telefono</li>
              <li>Numero tavolo (dato temporaneo, opzionale)</li>
              <li>Dati di navigazione anonimi (visualizzazioni piatti, punteggi quiz)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif-display text-xl font-bold">4. Modalità e conservazione</h2>
            <p className="text-sm text-[#3B3733]">I dati sono trattati con strumenti elettronici e conservati su server sicuri all'interno dello Spazio Economico Europeo. I dati raccolti per marketing sono conservati fino alla revoca del consenso o, comunque, per un massimo di <b>24 mesi</b> dall'ultima interazione utile. I dati di ordini e transazioni sono conservati per il tempo previsto dagli obblighi fiscali (10 anni).</p>
          </section>

          <section>
            <h2 className="font-serif-display text-xl font-bold">5. Comunicazione dei dati</h2>
            <p className="text-sm text-[#3B3733]">I dati non saranno diffusi né trasferiti a terzi al di fuori dell'UE. Potranno essere trattati da soggetti debitamente nominati <b>Responsabili del trattamento</b> ai sensi dell'art. 28 GDPR (fornitore piattaforma VOICEMENU, servizi cloud, commercialista, fornitori email marketing).</p>
          </section>

          <section>
            <h2 className="font-serif-display text-xl font-bold">6. Diritti dell'Interessato</h2>
            <p className="text-sm text-[#3B3733]">Ai sensi degli artt. 15-22 GDPR, l'Interessato ha diritto di:</p>
            <ul className="list-disc pl-5 text-sm text-[#3B3733] space-y-1 mt-2">
              <li>Accedere ai propri dati e richiederne copia;</li>
              <li>Chiedere la rettifica dei dati inesatti;</li>
              <li>Chiedere la cancellazione ("diritto all'oblio");</li>
              <li>Limitare o opporsi al trattamento;</li>
              <li>Ricevere i dati in formato strutturato (portabilità);</li>
              <li>Revocare il consenso in qualsiasi momento;</li>
              <li>Presentare reclamo all'<a className="text-[#C85A32] underline" href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">Autorità Garante per la Protezione dei Dati Personali</a>.</li>
            </ul>
            <p className="text-sm text-[#3B3733] mt-2">
              Per esercitare i propri diritti l'Interessato può contattare il Titolare {controller.email ? <>all'indirizzo <a className="text-[#C85A32] underline" href={`mailto:${controller.email}`}>{controller.email}</a></> : "ai recapiti indicati in questa pagina"}
              {controller.phone ? <> o al numero <a className="text-[#C85A32] underline" href={`tel:${controller.phone}`}>{controller.phone}</a></> : ""}.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-xl font-bold">7. Cookie e tecnologie simili</h2>
            <p className="text-sm text-[#3B3733]">La piattaforma utilizza esclusivamente cookie tecnici e di sessione necessari al funzionamento del servizio. Non vengono utilizzati cookie di profilazione di terze parti a fini pubblicitari senza consenso esplicito.</p>
          </section>

          <section>
            <h2 className="font-serif-display text-xl font-bold">8. Conferimento dei dati</h2>
            <p className="text-sm text-[#3B3733]">Il conferimento di nome e contatto è necessario per l'utilizzo dei servizi digitali del locale (chat, ordini al tavolo, quiz con premi). Il rilascio del consenso per finalità di marketing è invece <b>facoltativo</b>: la mancata prestazione non pregiudica la fruizione dei servizi essenziali.</p>
          </section>

          <section>
            <h2 className="font-serif-display text-xl font-bold">9. Modifiche all'informativa</h2>
            <p className="text-sm text-[#3B3733]">Il Titolare si riserva il diritto di aggiornare la presente informativa in caso di modifiche normative o tecniche. La versione aggiornata sarà pubblicata su questa pagina.</p>
          </section>

          <div className="mt-8 pt-6 border-t border-[#E8E2D5] text-xs text-[#6B625B] flex flex-wrap gap-2 justify-between">
            <span>Ultimo aggiornamento: <b>{today}</b></span>
            {controller.created_at && <span>Titolare registrato dal: <b>{fmtDate(controller.created_at)}</b></span>}
          </div>
        </article>
      </div>
    </div>
  );
}
